
import { IExceptionTelemetry } from '@microsoft/applicationinsights-common';
import { AuthError } from '@azure/msal-browser';
import {appInsights} from '../config/appInsights'
import {v4 as uuidv4} from 'uuid';

export class Logger{
    private static readonly AUTH_EXCEPTION_DEDUPLICATION_MS = 5 * 60 * 1000;
    private static readonly AUTH_EXCEPTION_CODES = [
        'block_iframe_reload',
        'refresh_token_expired',
        'timed_out'
    ];
    private static _instance: Logger;
    private readonly _authExceptionTimestamps = new Map<string, number>();
    private _sessionId: string = '';

    private constructor() {
        if (!this._sessionId) {
            this._sessionId = uuidv4();
        }
    }

    static get Instance() {
        if (!Logger._instance) {
            Logger._instance = new Logger();
        }

        return Logger._instance;
    }

    trackEvent(event: string, properties: {[key: string]: any}): void {
        appInsights.trackEvent({
            name: event,
            properties: {
              sessionId: this._sessionId,
              ...properties
            },
        });        
    }

    trackTrace(name: string, properties: {[key: string]: any}): void {
        appInsights.trackTrace({
            message: name,
            properties: {
              sessionId: this._sessionId,
              ...properties
            },
        });        
    }

    trackException(exception: IExceptionTelemetry, properties: {[key: string]: any}): void {
        const exceptionWithError = exception as IExceptionTelemetry & {
            error?: Error & { errorCode?: unknown };
            exception?: Error & { errorCode?: unknown };
        };
        const error = exceptionWithError.error ?? exceptionWithError.exception;
        const errorCode = error?.errorCode;
        const authExceptionCode = error instanceof AuthError && typeof errorCode === 'string'
            ? Logger.AUTH_EXCEPTION_CODES.find(code => code === errorCode)
            : undefined;

        if (authExceptionCode) {
            const now = Date.now();
            for (const [key, trackedAt] of this._authExceptionTimestamps) {
                if (now - trackedAt >= Logger.AUTH_EXCEPTION_DEDUPLICATION_MS) {
                    this._authExceptionTimestamps.delete(key);
                }
            }

            const deduplicationKey = `${authExceptionCode}:${properties.baseUrl ?? 'unknown'}`;
            const lastTrackedAt = this._authExceptionTimestamps.get(deduplicationKey);
            if (lastTrackedAt !== undefined && now - lastTrackedAt < Logger.AUTH_EXCEPTION_DEDUPLICATION_MS) {
                return;
            }

            this._authExceptionTimestamps.set(deduplicationKey, now);
        }

        appInsights.trackException(
            exception, 
            {
                sessionId: this._sessionId,
                ...properties
            }
        );        
    }
}